// Payment Plan

// Registration endpoint is included in adminApi below
// API Configuration for Admin Panel
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4901/api";
export const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:4901";

// Helper function to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  return `${SERVER_BASE_URL}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
};

// Helper function to process HTML content and convert relative image URLs to absolute
export const processHtmlContent = (html) => {
  if (!html) return "";

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Find all img tags and update their src
  const images = tempDiv.querySelectorAll("img");
  images.forEach((img) => {
    const src = img.getAttribute("src");
    if (src && !src.startsWith("http://") && !src.startsWith("https://")) {
      img.setAttribute("src", getImageUrl(src));
    }
  });

  return tempDiv.innerHTML;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      // A disabled account gets the server's explanation on the sign-in page instead of a silent bounce.
      const disabled = error.response?.data?.code === "account_disabled";
      window.location.href = disabled
        ? `/authentication/sign-in?error=${encodeURIComponent(error.response.data.error)}`
        : "/authentication/sign-in";
    }
    return Promise.reject(error);
  }
);

// Admin API Services
export const adminApi = {
  // User Management
  // Paginated fetch
  getUsers: (params = {}) => api.get("/users", { params }).then((res) => res.data),
  // Invite a new user (replaces the old password-based createUser)
  inviteUser: (userData) => api.post("/users/invite", userData).then((res) => res.data),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`).then((res) => res.data),
  updateUserPermissions: (
    id,
    permissions,
    isGlobalAdmin,
    notifyOnTwilioMessage,
    notifyOnMissedCall
  ) =>
    api
      .put(`/users/${id}/permissions`, {
        permissions,
        isGlobalAdmin,
        notifyOnTwilioMessage,
        notifyOnMissedCall,
      })
      .then((res) => res.data),
  // Disable (true) or re-enable (false) a user's sign-in — global admins only
  setUserDisabled: (id, disabled) =>
    api.put(`/users/${id}/disabled`, { disabled }).then((res) => res.data),
  // Authentication
  login: (credentials) => api.post("/auth/login", credentials).then((res) => res.data),
  getMe: () => api.get("/auth/me").then((res) => res.data),
  updateMe: (data) => api.put("/auth/me", data).then((res) => res.data),
  changePassword: (data) => api.put("/auth/me/password", data).then((res) => res.data),
  verifyInvite: (token) =>
    api.get("/auth/invite/verify", { params: { token } }).then((res) => res.data),

  // Pages Management
  getPages: () => api.get("/pages").then((res) => res.data),
  getPage: (id) => api.get(`/pages/${id}`).then((res) => res.data),
  createPage: (data) => api.post("/pages", data).then((res) => res.data),
  updatePage: (id, data) => api.put(`/pages/${id}`, data).then((res) => res.data),
  deletePage: (id) => api.delete(`/pages/${id}`).then((res) => res.data),

  // Services Management
  getServices: () => api.get("/services").then((res) => res.data),
  getService: (id) => api.get(`/services/${id}`).then((res) => res.data),
  createService: (data) => api.post("/services", data).then((res) => res.data),
  updateService: (id, data) => api.put(`/services/${id}`, data).then((res) => res.data),
  deleteService: (id) => api.delete(`/services/${id}`).then((res) => res.data),

  // Bootcamps Management
  getBootcamps: () => api.get("/bootcamps").then((res) => res.data),
  getBootcamp: (id) => api.get(`/bootcamps/${id}`).then((res) => res.data),
  // The bootcamp detail page (/bootcamps/:slug); the server looks bootcamps up by slug.
  getBootcampBySlug: (slug) =>
    api.get(`/bootcamps/${encodeURIComponent(slug)}`).then((res) => res.data),
  createBootcamp: (data) => api.post("/bootcamps", data).then((res) => res.data),
  updateBootcamp: (id, data) => api.put(`/bootcamps/${id}`, data).then((res) => res.data),
  deleteBootcamp: (id) => api.delete(`/bootcamps/${id}`).then((res) => res.data),
  getBootcampApplications: (params) =>
    api.get("/bootcamp-applications", { params }).then((res) => res.data),
  exportBootcampApplications: (params) =>
    api.get("/bootcamp-applications/export", { params }).then((res) => res.data),

  // Blog Management
  getBlogPosts: (params) => api.get("/blog", { params }).then((res) => res.data),
  getBlogPost: (id) => api.get(`/blog/${id}`).then((res) => res.data),
  createBlogPost: (data) => api.post("/blog", data).then((res) => res.data),
  updateBlogPost: (id, data) => api.put(`/blog/${id}`, data).then((res) => res.data),
  deleteBlogPost: (id) => api.delete(`/blog/${id}`).then((res) => res.data),
  getBlogCategories: () => api.get("/blog/categories/all").then((res) => res.data),
  getBlogTags: () => api.get("/blog/tags/all").then((res) => res.data),
  createBlogCategory: (data) => api.post("/blog/categories", data).then((res) => res.data),
  updateBlogCategory: (id, data) => api.put(`/blog/categories/${id}`, data).then((res) => res.data),
  deleteBlogCategory: (id) => api.delete(`/blog/categories/${id}`).then((res) => res.data),

  // Team Management
  getTeamMembers: () => api.get("/team").then((res) => res.data),
  createTeamMember: (data) => api.post("/team", data).then((res) => res.data),
  updateTeamMember: (id, data) => api.put(`/team/${id}`, data).then((res) => res.data),
  deleteTeamMember: (id) => api.delete(`/team/${id}`).then((res) => res.data),

  // Testimonials Management
  getTestimonials: () => api.get("/testimonials").then((res) => res.data),
  createTestimonial: (data) => api.post("/testimonials", data).then((res) => res.data),
  updateTestimonial: (id, data) => api.put(`/testimonials/${id}`, data).then((res) => res.data),
  deleteTestimonial: (id) => api.delete(`/testimonials/${id}`).then((res) => res.data),

  // Form Submissions
  getContactSubmissions: (params) =>
    api.get("/contact-submissions", { params }).then((res) => res.data),
  exportContactSubmissions: (params) =>
    api.get("/contact-submissions/export", { params }).then((res) => res.data),

  // Consultations Management
  getConsultations: (params) => api.get("/consultations", { params }).then((res) => res.data),
  exportConsultations: (params) =>
    api.get("/consultations/export", { params }).then((res) => res.data),
  getConsultation: (id) => api.get(`/consultations/${id}`).then((res) => res.data),
  updateConsultation: (id, data) => api.put(`/consultations/${id}`, data).then((res) => res.data),
  deleteConsultation: (id) => api.delete(`/consultations/${id}`).then((res) => res.data),
  createTeamsMeeting: (data) => api.post("/teams/meetings", data).then((res) => res.data),
  getTeamsMeetings: (params) => api.get("/teams/meetings", { params }).then((res) => res.data),
  rescheduleTeamsMeeting: (id, data) =>
    api.put(`/teams/meetings/${id}/reschedule`, data).then((res) => res.data),
  deleteTeamsMeeting: (id) => api.delete(`/teams/meetings/${id}`).then((res) => res.data),

  // Clarity Call Bookings Management
  getClarityCallBookings: (params) =>
    api.get("/clarity-call-bookings", { params }).then((res) => res.data),
  exportClarityCallBookings: (params) =>
    api.get("/clarity-call-bookings/export", { params }).then((res) => res.data),
  getClarityCallBooking: (id) => api.get(`/clarity-call-bookings/${id}`).then((res) => res.data),
  updateClarityCallBookingStatus: (id, status) =>
    api.patch(`/clarity-call-bookings/${id}/status`, { status }).then((res) => res.data),
  sendMessageToClarityCallBooker: (id, data) =>
    api.post(`/clarity-call-bookings/${id}/message`, data).then((res) => res.data),

  // Clarity Call Availability
  getClarityCallAvailability: () => api.get("/clarity-call-availability").then((res) => res.data),
  saveClarityCallAvailability: (rules) =>
    api.put("/clarity-call-availability", rules).then((res) => res.data),
  previewClarityCallAvailability: (rules) =>
    api.post("/clarity-call-availability/preview", rules).then((res) => res.data),

  // Notifications Management
  getNotifications: (params) => api.get("/notifications", { params }).then((res) => res.data),
  getUnreadCount: () => api.get("/notifications/unread-count").then((res) => res.data),
  markNotificationRead: (id) => api.put(`/notifications/${id}/read`).then((res) => res.data),
  markAllNotificationsRead: () => api.put("/notifications/mark-all-read").then((res) => res.data),
  deleteNotification: (id) => api.delete(`/notifications/${id}`).then((res) => res.data),
  deleteReadNotifications: () => api.delete("/notifications").then((res) => res.data),

  // Notifications Management
  getNotifications: (params) => api.get("/notifications", { params }).then((res) => res.data),
  getUnreadCount: () => api.get("/notifications/unread-count").then((res) => res.data),
  markNotificationRead: (id) => api.put(`/notifications/${id}/read`).then((res) => res.data),
  markAllNotificationsRead: () => api.put("/notifications/mark-all-read").then((res) => res.data),
  deleteNotification: (id) => api.delete(`/notifications/${id}`).then((res) => res.data),
  deleteReadNotifications: () => api.delete("/notifications").then((res) => res.data),

  // Customer Chat (Twilio SMS/WhatsApp)
  getChatConversations: (params) => api.get("/chat/conversations", { params }).then((r) => r.data),
  getChatMessages: (conversationId) =>
    api.get(`/chat/conversations/${conversationId}/messages`).then((r) => r.data),
  sendChatMessage: (conversationId, text) =>
    api.post(`/chat/conversations/${conversationId}/messages`, { text }).then((r) => r.data),
  startChatConversation: (channel, customerPhone, text) =>
    api.post("/chat/conversations", { channel, customerPhone, text }).then((r) => r.data),
  markConversationRead: (conversationId) =>
    api.post(`/chat/conversations/${conversationId}/read`).then((r) => r.data),
  getChatMedia: (messageId, index) =>
    api.get(`/chat/media/${messageId}/${index}`, { responseType: "blob" }).then((r) => r.data),

  // Voice Calling (Twilio Voice)
  getVoiceToken: () => api.get("/voice/token").then((r) => r.data),
  getCalls: (page = 1, limit = 30) =>
    api.get(`/voice/calls?page=${page}&limit=${limit}`).then((r) => r.data),
  getCallVoicemail: (callId) =>
    api.get(`/voice/calls/${callId}/voicemail`, { responseType: "blob" }).then((r) => r.data),

  // Settings Management
  getSettings: () => api.get("/settings").then((res) => res.data),
  getSettingsMap: () => api.get("/settings/map").then((res) => res.data),
  getSetting: (name) => api.get(`/settings/${name}`).then((res) => res.data),
  upsertSetting: (data) => api.post("/settings", data).then((res) => res.data),
  updateSetting: (id, data) => api.put(`/settings/${id}`, data).then((res) => res.data),
  deleteSetting: (id) => api.delete(`/settings/${id}`).then((res) => res.data),
  bulkUpdateSettings: (settings) =>
    api.post("/settings/bulk", { settings }).then((res) => res.data),

  // Statistics
  getStatistics: () => api.get("/statistics").then((res) => res.data),

  // Autoresponder Campaigns
  getCampaigns: () => api.get("/autoresponder").then((r) => r.data),
  getCampaignLogs: (params) => api.get("/autoresponder/logs", { params }).then((r) => r.data),
  createCampaign: (data) => api.post("/autoresponder", data).then((r) => r.data),
  updateCampaign: (id, data) => api.put(`/autoresponder/${id}`, data).then((r) => r.data),
  deleteCampaign: (id) => api.delete(`/autoresponder/${id}`).then((r) => r.data),
  uploadStepAttachment: (campaignId, stepId, formData) =>
    api
      .post(`/autoresponder/steps/${campaignId}/${stepId}/attachment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  deleteStepAttachment: (campaignId, stepId, filename) =>
    api
      .delete(`/autoresponder/steps/${campaignId}/${stepId}/attachment/${filename}`)
      .then((r) => r.data),

  // Webinar Registrations
  getWebinarRegistrations: (params) =>
    api.get("/webinar-registrations", { params }).then((res) => res.data),
  exportWebinarRegistrations: (params) =>
    api.get("/webinar-registrations/export", { params }).then((res) => res.data),
  getWebinarRegistration: (id) => api.get(`/webinar-registrations/${id}`).then((res) => res.data),
  sendMessageToWebinarRegistrant: (id, data) =>
    api.post(`/webinar-registrations/${id}/message`, data).then((res) => res.data),
  updateWebinarRegistrationStatus: (id, status) =>
    api.patch(`/webinar-registrations/${id}/status`, { status }).then((res) => res.data),

  // Landing Page Applications
  getLandingApplications: (params) =>
    api.get("/landing-applications", { params }).then((res) => res.data),
  exportLandingApplications: (params) =>
    api.get("/landing-applications/export", { params }).then((res) => res.data),
  getLandingApplication: (id) => api.get(`/landing-applications/${id}`).then((res) => res.data),
  sendMessageToApplicant: (id, data) =>
    api.post(`/landing-applications/${id}/message`, data).then((res) => res.data),
  updateApplicationStatus: (id, status) =>
    api.patch(`/landing-applications/${id}/status`, { status }).then((res) => res.data),

  // Newsletter Management
  getNewsletterSubscribers: (params) => api.get("/newsletter", { params }).then((res) => res.data),
  exportNewsletterSubscribers: (params) =>
    api.get("/newsletter/export", { params }).then((res) => res.data),
  sendNewsletter: (data) => api.post("/newsletter/send", data).then((res) => res.data),
  deleteNewsletterSubscriber: (id) => api.delete(`/newsletter/${id}`).then((res) => res.data),

  // File Upload
  uploadFile: (formData) => {
    // Accept FormData directly (do not wrap file again)
    return api
      .post("/upload/single", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  uploadFiles: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return api
      .post("/upload/multiple", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  // Image Upload (uses FormData directly from component)
  uploadImage: (formData) => {
    return api
      .post("/upload/single", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  // File Asset Library
  listFileAssets: () => api.get("/files").then((r) => r.data),
  uploadFileAsset: (formData) =>
    api
      .post("/files", formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data),
  updateFileAssetLabel: (slug, label) => api.patch(`/files/${slug}`, { label }).then((r) => r.data),
  deleteFileAsset: (slug) => api.delete(`/files/${slug}`).then((r) => r.data),

  // Bulk Messaging
  getBulkMessageSources: () => api.get("/bulk-message/sources").then((r) => r.data),
  getAudience: (data) => api.post("/bulk-message/audience", data).then((r) => r.data),
  previewBulkMessage: (data) => api.post("/bulk-message/preview", data).then((r) => r.data),
  testBulkMessage: (data) => api.post("/bulk-message/test", data).then((r) => r.data),
  sendBulkMessage: (data) => api.post("/bulk-message/send", data).then((r) => r.data),

  // Generated Payment Links
  getGeneratedPaymentLinks: () => api.get("/admin/payment-links").then((res) => res.data),
  generatePaymentLink: (data) =>
    api.post("/admin/payment-links/generate", data).then((res) => res.data),
  deleteGeneratedPaymentLink: (id) =>
    api.delete(`/admin/payment-links/${id}`).then((res) => res.data),
  resendGeneratedPaymentLink: (id) =>
    api.post(`/admin/payment-links/${id}/resend`).then((res) => res.data),
  getOpenPaymentLinks: () => api.get("/admin/open-payment-links").then((res) => res.data),
  createOpenPaymentLink: (data) =>
    api.post("/admin/open-payment-links", data).then((res) => res.data),
  updateOpenPaymentLink: (id, data) =>
    api.patch(`/admin/open-payment-links/${id}`, data).then((res) => res.data),
  deleteOpenPaymentLink: (id) =>
    api.delete(`/admin/open-payment-links/${id}`).then((res) => res.data),
  getOpenPaymentLinkPayers: (id) =>
    api.get(`/admin/open-payment-links/${id}/payers`).then((res) => res.data),

  // Timesheet Projects
  getTimesheetProjects: () => api.get("/timesheet/projects").then((r) => r.data),
  createTimesheetProject: (data) => api.post("/timesheet/projects", data).then((r) => r.data),
  updateTimesheetProject: (id, data) =>
    api.put(`/timesheet/projects/${id}`, data).then((r) => r.data),
  deleteTimesheetProject: (id) => api.delete(`/timesheet/projects/${id}`).then((r) => r.data),

  // Timesheet Entries
  getTimesheetEntries: (params) => api.get("/timesheet/entries", { params }).then((r) => r.data),
  createTimesheetEntry: (data) => api.post("/timesheet/entries", data).then((r) => r.data),
  updateTimesheetEntry: (id, data) => api.put(`/timesheet/entries/${id}`, data).then((r) => r.data),
  deleteTimesheetEntry: (id) => api.delete(`/timesheet/entries/${id}`).then((r) => r.data),

  // Payment Management
  getPayments: (params) => api.get("/admin/payments", { params }).then((res) => res.data),
  getPaymentStats: () => api.get("/admin/payments/stats").then((res) => res.data),
  refundPayment: (id, data) =>
    api.post(`/admin/payments/${id}/refund`, data).then((res) => res.data),
  retryPayment: (id) => api.post(`/admin/payments/${id}/retry`).then((res) => res.data),
  getPaymentHistory: (id) => api.get(`/admin/payments/${id}/history`).then((res) => res.data),
  getGeneratedLinkHistory: (id) =>
    api.get(`/admin/payments/link/${id}/history`).then((res) => res.data),
  refundGeneratedPayment: (id, data) =>
    api.post(`/admin/payments/link/${id}/refund`, data).then((res) => res.data),
  cancelLinkSubscription: (id) =>
    api.post(`/admin/payments/link/${id}/cancel-subscription`).then((res) => res.data),
  reconcileSubscriptions: () =>
    api.post("/admin/payments/reconcile-subscriptions").then((res) => res.data),
  reconcileLinkSubscription: (id) =>
    api.post(`/admin/payments/link/${id}/reconcile`).then((res) => res.data),
  exportPayments: (params) => {
    return api
      .get("/admin/payments/export", {
        params,
        responseType: "blob", // Important for file download
      })
      .then((res) => res.data);
  },
};

export default adminApi;
